import { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import ChangeContextFields from '@/frontend/features/Fde/ChangeContextFields'
import ElementPropertyFields from '@/frontend/features/Fde/ElementPropertyFields'
import { FdeEditorDialog } from '@/frontend/features/Fde/FdeEditorSurface'
import ProfileImageField from '@/frontend/features/Fde/ProfileImageField'
import { SelectControl, TextareaControl, TextControl } from '@/frontend/features/Fde/FormControls'
import { propertiesForClass } from '@/frontend/features/Fde/ontologyProperties'
import type { ElementProperty, Evidence, FdeCopy, ModelElement, OntologyClass } from '@/frontend/features/Fde/types'
import { fdeRoutes } from '@/frontend/features/Fde/routes'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  element: ModelElement | null
  projectId: string
  ontologyClasses: OntologyClass[]
  evidences: Evidence[]
  copy: FdeCopy
  allowedLineageKey?: string
  returnTo?: 'people'
}

export default function ElementEditorDialog({ open, onOpenChange, element, projectId, ontologyClasses, evidences, copy, allowedLineageKey, returnTo }: Props) {
  const form = useForm<{ ontology_class_id: string; name: string; description: string; aliases: string; properties: ElementProperty[]; status: string; change_note: string; evidence_id: string; profile_image: File | null; remove_profile_image: boolean }>({ ontology_class_id: '', name: '', description: '', aliases: '', properties: [], status: 'confirmado', change_note: '', evidence_id: '', profile_image: null, remove_profile_image: false })
  const availableClasses = ontologyClasses.filter((ontologyClass) => !ontologyClass.abstract && (!allowedLineageKey || ontologyClass.lineage.includes(allowedLineageKey)))
  const classOptions = availableClasses.map((ontologyClass) => ({ value: ontologyClass.id, label: `${ontologyClass.family.name} · ${ontologyClass.name}` }))
  const selectedClass = ontologyClasses.find((ontologyClass) => ontologyClass.id === form.data.ontology_class_id)

  useEffect(() => {
    if (!open) return
    form.setData({
      ontology_class_id: element?.ontology_class?.id ?? availableClasses[0]?.id ?? '',
      name: element?.name ?? '',
      description: element?.description ?? '',
      aliases: element?.aliases.join(', ') ?? '',
      properties: propertiesForClass(ontologyClasses.find((ontologyClass) => ontologyClass.id === (element?.ontology_class?.id ?? availableClasses[0]?.id)), element),
      status: element?.status ?? 'confirmado',
      change_note: '',
      evidence_id: '',
      profile_image: null,
      remove_profile_image: false,
    })
  }, [open, element?.id])

  function submit() {
    form.transform((data) => ({ element: { ...data, aliases: data.aliases.split(',').map((value) => value.trim()).filter(Boolean) } }))
    const options = { preserveScroll: true, forceFormData: Boolean(form.data.profile_image), onSuccess: () => onOpenChange(false) }
    const query = `?${new URLSearchParams({ project_id: projectId, ...(returnTo ? { return_to: returnTo } : {}) }).toString()}`
    if (element) form.patch(`${fdeRoutes.objects}/${element.id}${query}`, options)
    else form.post(`${fdeRoutes.objects}${query}`, options)
  }

  return (
    <FdeEditorDialog
      open={open}
      onOpenChange={onOpenChange}
      title={element ? copy.workspace.edit_element : copy.workspace.add_element}
      closeLabel={copy.common.close}
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>{copy.common.cancel}</Button><Button disabled={form.processing || !form.data.ontology_class_id || !form.data.name.trim()} onClick={submit}>{copy.common.save}</Button></>}
    >
        <div className="grid gap-5 sm:grid-cols-2">
          <SelectControl label={copy.forms.ontology_class} value={form.data.ontology_class_id} onChange={(value) => form.setData((current) => ({ ...current, ontology_class_id: value, properties: propertiesForClass(ontologyClasses.find((ontologyClass) => ontologyClass.id === value), element) }))} options={classOptions} placeholder={copy.forms.select_ontology_class} />
          <TextControl label={copy.forms.name} value={form.data.name} onChange={(event) => form.setData('name', event.target.value)} required />
          <div className="sm:col-span-2"><TextareaControl label={copy.forms.description} value={form.data.description} onChange={(event) => form.setData('description', event.target.value)} rows={3} /></div>
          <TextControl label={copy.forms.aliases} value={form.data.aliases} onChange={(event) => form.setData('aliases', event.target.value)} />
          {selectedClass?.lineage.includes('pessoa') && <div className="sm:col-span-2"><ProfileImageField currentImage={element?.image_url || null} file={form.data.profile_image} removed={form.data.remove_profile_image} copy={copy} onChange={(file, removed) => form.setData((current) => ({ ...current, profile_image: file, remove_profile_image: removed }))} /></div>}
          <div className="sm:col-span-2"><ElementPropertyFields schema={selectedClass?.property_schema || []} values={form.data.properties} copy={copy} onChange={(values) => form.setData('properties', values)} /></div>
          <SelectControl label={copy.forms.status} value={form.data.status} onChange={(value) => form.setData('status', value)} options={[{ value: 'proposto', label: copy.workspace.proposed }, { value: 'confirmado', label: copy.workspace.confirmed_status }]} />
          <ChangeContextFields className="sm:col-span-2" changeNote={form.data.change_note} evidenceId={form.data.evidence_id} evidences={evidences} copy={copy} onChangeNote={(value) => form.setData('change_note', value)} onEvidenceId={(value) => form.setData('evidence_id', value)} />
        </div>
    </FdeEditorDialog>
  )
}
